import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentCheck,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineArrowPath,
} from "react-icons/hi2";
import SharedModal from "../components/common/SharedModal";
import { useNavigate } from "react-router-dom";
import {
  authService,
  breakdownLogService,
  machineService,
  plantService,
  lineService,
  masterDataService,
  workOrderService,
  userService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import { toast } from "react-toastify";
import PageLoading from "../components/common/PageLoading";
import "./BreakdownLog.css";

const initialFormState = {
  plantId: "",
  lineId: "",
  machineId: "",
  breakdownStartTime: "",
  breakdownEndTime: "",
  failureDescription: "",
  failureCategory: "",
  reportedBy: 0,
  statusId: "",
};

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedBreakdownLogData = () => {
  try {
    const cachedLogs = sessionStorage.getItem("breakdown_logs_data");
    const cachedMachines = sessionStorage.getItem("machines_data");
    const cachedPlants = sessionStorage.getItem("plants_data");
    const cachedLines = sessionStorage.getItem("lines_data");
    const cachedStatuses = sessionStorage.getItem(
      "breakdown_log_statuses_data"
    );

    const cachedStatusesRaw = cachedStatuses ? JSON.parse(cachedStatuses) : [];
    
    // Deduplicate cached statuses immediately
    const deduplicatedStatuses = cachedStatusesRaw.reduce((acc, current) => {
      const name = current.statusName?.trim().toLowerCase();
      const x = acc.find(item => item.statusName?.trim().toLowerCase() === name);
      if (!x) return acc.concat([current]);
      return acc;
    }, []);

    return {
      logs: cachedLogs ? JSON.parse(cachedLogs) : [],
      machines: cachedMachines ? JSON.parse(cachedMachines) : [],
      plants: cachedPlants ? JSON.parse(cachedPlants) : [],
      lines: cachedLines ? JSON.parse(cachedLines) : [],
      statuses: deduplicatedStatuses,
      hasAll: !!(
        cachedLogs &&
        cachedMachines &&
        cachedPlants &&
        cachedLines &&
        cachedStatuses
      ),
    };
  } catch (e) {
    return {
      logs: [],
      machines: [],
      plants: [],
      lines: [],
      statuses: [],
      hasAll: false,
    };
  }
};

const BreakdownLog = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const user = authService.getUser();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("BreakdownLog.View");
  const canCreate = hasPermission("BreakdownLog.Create");
  const canEdit = hasPermission("BreakdownLog.Edit");
  const canDelete = hasPermission("BreakdownLog.Delete");

  console.log("canView", canView);
  console.log("canCreate", canCreate);
  console.log("canEdit", canEdit);
  console.log("canDelete", canDelete);

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedBreakdownLogData();
  const [breakdownLogs, setBreakdownLogs] = useState(cachedData.logs);
  const [machines, setMachines] = useState(cachedData.machines);
  const [plants, setPlants] = useState(cachedData.plants);
  const [lines, setLines] = useState(cachedData.lines);
  const [statuses, setStatuses] = useState(cachedData.statuses);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    plantId: "",
    lineId: "",
    machineId: "",
    startDate: "",
    endDate: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedBreakdownLogData();
      if (refreshedCachedData.hasAll) {
        setBreakdownLogs(refreshedCachedData.logs);
        setMachines(refreshedCachedData.machines);
        setPlants(refreshedCachedData.plants);
        setLines(refreshedCachedData.lines);
        
        // Final deduplication on mount for safety
        const deduplicatedOnMount = (refreshedCachedData.statuses || []).reduce((acc, current) => {
            const name = current.statusName?.trim().toLowerCase();
            const x = acc.find(item => item.statusName?.trim().toLowerCase() === name);
            if (!x) return acc.concat([current]);
            return acc;
        }, []);
        setStatuses(deduplicatedOnMount);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && canView) {
      // Check if we have cached data - if yes, show page immediately
      const hasCachedData =
        sessionStorage.getItem("breakdown_logs_data") &&
        sessionStorage.getItem("machines_data") &&
        sessionStorage.getItem("plants_data") &&
        sessionStorage.getItem("lines_data") &&
        sessionStorage.getItem("breakdown_log_statuses_data");

      // If we have cached data, don't show loading - load fresh data in background
      if (hasCachedData) {
        setLoading(false);
      }

      // Load fresh data in background
      loadInitialData();
    } else if (!canView) {
      const msg = "You do not have permission to view Breakdown Log records";
      setError(msg);
      toast.error(msg);
      setLoading(false);
    } else {
      const msg = "Please login to manage breakdown logs";
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("breakdown_logs_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const [
        logsData,
        machinesData,
        plantsData,
        linesData,
        statusesData,
        usersData,
      ] = await Promise.all([
        breakdownLogService.getBreakdownLogs(),
        machineService.getAllMachines(),
        plantService.getAllPlants(),
        lineService.getAllLines(),
        masterDataService.getStatuses(tenantId).catch(() => []),
        userService.getAllUsers().catch(() => []),
      ]);

      setMachines(machinesData || []);
      setPlants(plantsData || []);
      setLines(linesData || []);
      setUsers(usersData || []);

      // Auto-select plant for filter and apply local filtering
      const resolvedPlantId = plantsData?.length === 1 ? plantsData[0].plantId.toString() : "";
      if (resolvedPlantId) {
        setFilters((prev) => ({ ...prev, plantId: resolvedPlantId }));
      }

      let filteredLogs = logsData || [];
      if (resolvedPlantId) {
        filteredLogs = filteredLogs.filter((log) => {
          const machine = machinesData.find((m) => m.machineId === log.machineId);
          if (!machine) return log.plantId?.toString() === resolvedPlantId;
          const line = linesData.find((l) => l.lineId === machine.lineId);
          return (
            log.plantId?.toString() === resolvedPlantId ||
            line?.plantId?.toString() === resolvedPlantId
          );
        });
      }
      setBreakdownLogs(filteredLogs);

      // Filter statuses - only show BreakdownLog statuses and deduplicate by name (case-insensitive & trimmed)
      const actionStatuses = (statusesData || [])
        .filter((s) => s.statusType === "BreakdownLog")
        .reduce((acc, current) => {
          const name = current.statusName?.trim().toLowerCase();
          const x = acc.find(item => item.statusName?.trim().toLowerCase() === name);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
      
      setStatuses(actionStatuses);

      // Cache the data
      sessionStorage.setItem(
        "breakdown_logs_data",
        JSON.stringify(logsData || [])
      );
      sessionStorage.setItem(
        "machines_data",
        JSON.stringify(machinesData || [])
      );
      sessionStorage.setItem("plants_data", JSON.stringify(plantsData || []));
      sessionStorage.setItem("lines_data", JSON.stringify(linesData || []));
      sessionStorage.setItem(
        "breakdown_log_statuses_data",
        JSON.stringify(actionStatuses)
      );

      if (actionStatuses.length === 0) {
        console.warn("No Breakdown log statuses found. Auto-creating defaults.");
        // Auto-create defaults only if they don't exist in the broader list (case-insensitive check)
        const hasActive = (statusesData || []).some(s => 
            s.statusType === "BreakdownLog" && 
            s.statusName?.trim().toLowerCase() === "active"
        );
        const hasInactive = (statusesData || []).some(s => 
            s.statusType === "BreakdownLog" && 
            s.statusName?.trim().toLowerCase() === "inactive"
        );

        try {
            if (!hasActive) {
                await masterDataService.createStatus({ tenantId, statusType: "BreakdownLog", statusName: "Active", isFinal: false, status: "Active" });
            }
            if (!hasInactive) {
                await masterDataService.createStatus({ tenantId, statusType: "BreakdownLog", statusName: "Inactive", isFinal: true, status: "Active" });
            }
            
            if (!hasActive || !hasInactive) {
                // Refetch after creation
                const updatedStatusesData = await masterDataService.getStatuses(tenantId).catch(() => []);
                const updatedActionStatuses = updatedStatusesData
                    .filter(s => s.statusType === "BreakdownLog")
                    .reduce((acc, current) => {
                        const name = current.statusName?.trim().toLowerCase();
                        const x = acc.find(item => item.statusName?.trim().toLowerCase() === name);
                        if (!x) return acc.concat([current]);
                        return acc;
                    }, []);
                setStatuses(updatedActionStatuses);
                sessionStorage.setItem("breakdown_log_statuses_data", JSON.stringify(updatedActionStatuses));
            }
        } catch (createErr) {
            console.error("Failed to auto-create statuses:", createErr);
        }
      }
    } catch (err) {
      console.error("Error loading breakdown logs:", err);
      const msg = "Failed to load breakdown logs. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogsWithFilters = async () => {
    try {
      setRefreshing(true);
      setError("");

      const machineId = filters.machineId
        ? parseInt(filters.machineId, 10)
        : null;
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      let data = await breakdownLogService.getBreakdownLogs(
        machineId,
        startDate,
        endDate
      );

      // Apply Plant and Line filters on client side
      if (filters.plantId || filters.lineId) {
        data = (data || []).filter((log) => {
          const machine = machines.find((m) => m.machineId === log.machineId);
          if (!machine) return false;

          const line = lines.find((l) => l.lineId === machine.lineId);

          // Filter by Plant
          if (filters.plantId) {
            const plantMatch =
              log.plantId?.toString() === filters.plantId ||
              line?.plantId?.toString() === filters.plantId;
            if (!plantMatch) return false;
          }

          // Filter by Line
          if (filters.lineId) {
            if (machine.lineId?.toString() !== filters.lineId) return false;
          }

          return true;
        });
      }

      setBreakdownLogs(data || []);
    } catch (err) {
      console.error("Error fetching breakdown logs:", err);
      setError("Failed to fetch breakdown logs. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchLogsWithFilters();
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    setFilters({
      plantId: "",
      lineId: "",
      machineId: "",
      startDate: "",
      endDate: "",
    });
    loadInitialData();
  };

  const getMachineName = (machineId) => {
    if (!machineId) return "-";
    const m = machines.find((x) => x.machineId === machineId);
    return m ? `${m.machineName} (${m.machineCode})` : `Machine ${machineId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return d.toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getUserName = (userId) => {
    // If no userId or userId is 0, assume it's created by Admin or Unknown
    if (!userId || userId === 0) {
      // Try to find a user with username 'admin' or similar, otherwise hardcode
      const adminUser = users.find((u) =>
        u.username.toLowerCase().includes("admin")
      );
      return adminUser ? adminUser.username : "Admin";
    }

    // Try to find user by ID (handling string/number mismatch)
    const u = users.find((x) => String(x.userId) === String(userId));
    if (u) return u.username;

    // Fallback: If ID is 1 and user not found, return Admin
    if (String(userId) === "1") return "Admin";

    return userId;
  };

  const handleAdd = () => {
    if (!canCreate) {
      setError("You do not have permission to create Breakdown Log records");
      return;
    }
    setEditingLog(null);
    
    // Find default Active status
    const activeStatus = statuses.find(s => s.statusName === 'Active');
    
    // Auto-select plant for form
    let initialPlantId = "";
    if (plants?.length === 1) {
      initialPlantId = plants[0].plantId.toString();
    }

    setFormData({
      ...initialFormState,
      plantId: initialPlantId,
      reportedBy: user?.userId || 0,
      statusId: activeStatus ? activeStatus.statusId : "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (log) => {
    if (!canEdit) {
      setError("You do not have permission to edit Breakdown Log records");
      return;
    }
    setEditingLog(log);
    // Find machine to get lineId
    const machine = machines.find((m) => m.machineId === log.machineId);
    const lineId = machine?.lineId || "";
    const plantId =
      log.plantId ||
      (machine?.lineId
        ? lines.find((l) => l.lineId === machine.lineId)?.plantId
        : "") ||
      "";

    setFormData({
      plantId: plantId ? plantId.toString() : "",
      lineId: lineId ? lineId.toString() : "",
      machineId: log.machineId || "",
      breakdownStartTime: log.breakdownStartTime
        ? new Date(log.breakdownStartTime).toISOString().slice(0, 16)
        : "",
      breakdownEndTime: log.breakdownEndTime
        ? new Date(log.breakdownEndTime).toISOString().slice(0, 16)
        : "",
      failureDescription: log.failureDescription || "",
      failureCategory: log.failureCategory || "",
      reportedBy: log.reportedBy || user?.userId || 0,
      statusId: log.statusId || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError("You do not have permission to delete Breakdown Log records");
      return;
    }
    try {
      await breakdownLogService.deleteBreakdownLog(id);
      await fetchLogsWithFilters();
      setDeleteConfirm(null);
      toast.success("Breakdown log deleted successfully!");
    } catch (err) {
      console.error("Error deleting breakdown log:", err);
      const msg = "Failed to delete breakdown log. Please try again.";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleCreateWorkOrder = async (breakdownLogId) => {
    try {
      const breakdownLog = breakdownLogs.find(
        (b) => b.breakdownLogId === breakdownLogId
      );
      if (!breakdownLog) {
        setError("Breakdown log not found");
        return;
      }

      // Navigate to work order creation with breakdown log ID
      toast.info("Preparing work order wizard...");
      navigate(`/home/work-order-entry?breakdownLogId=${breakdownLogId}`);
    } catch (err) {
      console.error("Error creating work order from breakdown:", err);
      const msg = "Failed to create work order. Please try again.";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleFiveWhy = (log) => {
    navigate("/home/rca/five-whys", {
      state: {
        machineId: log.machineId,
        plantId: log.plantId,
        description:
          log.failureDescription ||
          `Breakdown for ${getMachineName(log.machineId)}`,
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Cascading resets: Plant → Line → Machine
      if (name === "plantId") {
        next.lineId = "";
        next.machineId = "";
      } else if (name === "lineId") {
        next.machineId = "";
      }
      return next;
    });

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.machineId) {
      errors.machineId = "Machine is required";
    }

    if (!formData.breakdownStartTime) {
      errors.breakdownStartTime = "Breakdown start time is required";
    }

    if (!formData.breakdownEndTime) {
      errors.breakdownEndTime = "Breakdown end time is required";
    }

    if (formData.breakdownStartTime && formData.breakdownEndTime) {
      const startTime = new Date(formData.breakdownStartTime);
      const endTime = new Date(formData.breakdownEndTime);
      if (endTime <= startTime) {
        errors.breakdownEndTime = "End time must be after start time";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingLog && !canEdit) {
      setError("You do not have permission to edit Breakdown Log records");
      return;
    }
    if (!editingLog && !canCreate) {
      setError("You do not have permission to create Breakdown Log records");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const payload = {
        machineId: parseInt(formData.machineId, 10),
        plantId: formData.plantId ? parseInt(formData.plantId, 10) : null,
        breakdownStartTime: new Date(formData.breakdownStartTime).toISOString(),
        breakdownEndTime: new Date(formData.breakdownEndTime).toISOString(),
        failureDescription: formData.failureDescription || null,
        failureCategory: formData.failureCategory || null,
        reportedBy: formData.reportedBy || user?.userId || 0,
        statusId: formData.statusId ? parseInt(formData.statusId, 10) : null,
      };

      const toastId = toast.loading(editingLog ? "Updating breakdown log..." : "Creating breakdown log...");
      if (editingLog) {
        await breakdownLogService.updateBreakdownLog(
          editingLog.breakdownLogId,
          payload
        );
      } else {
        await breakdownLogService.createBreakdownLog(payload);
      }

      await fetchLogsWithFilters();
      setShowModal(false);
      setFormData(initialFormState);
      toast.update(toastId, { render: editingLog ? "Breakdown log updated successfully!" : "Breakdown log created successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error saving breakdown log:", err);
      const msg = err.response?.data?.message || "Failed to save breakdown log. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer">
        <PageLoading message="Loading breakdown logs..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Breakdown Logs</h1>
          <p>Track and manage machine breakdowns and failures</p>
        </div>
        <div className="header-actions">
          {canCreate && (
            <button className="btn-primary" onClick={handleAdd}>
              <HiOutlinePlus />
              New Breakdown
            </button>
          )}
          <button
            className="btn-secondary"
            onClick={fetchLogsWithFilters}
            disabled={refreshing}
          >
            <HiOutlineArrowPath className={refreshing ? "spinning" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10">
        <div className="table-header">
          <button
            className="filter-toggle"
            type="button"
            onClick={() => setShowFilterDrawer(true)}
          >
            <HiOutlineAdjustmentsHorizontal />
            <span>Filters</span>
          </button>
          <div className="table-info">
            {breakdownLogs.length}{" "}
            {breakdownLogs.length === 1 ? "record" : "records"}
          </div>
        </div>

        {breakdownLogs.length === 0 ? (
          <div className="empty-state">
            <h3>No breakdown logs found</h3>
            <p>
              {canCreate
                ? "Start by creating your first breakdown log"
                : "No breakdown logs found"}
            </p>
            {canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus />
                Create First Breakdown
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Machine</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Duration</th>
                <th>Failure Category</th>
                <th>Description</th>
                <th>Status</th>
                <th>RCA</th>
                <th style={{ width: "115px", }}>Reported By</th>
              </tr>
            </thead>
            <tbody>
              {breakdownLogs.map((log) => (
                <tr key={log.breakdownLogId}>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(log)}
                          title="Edit breakdown"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      <button
                        className="btn-icon-edit"
                        onClick={() =>
                          handleCreateWorkOrder(log.breakdownLogId)
                        }
                        title="Create Work Order"
                      >
                        <HiOutlineClipboardDocumentCheck />
                      </button>
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(log)}
                          title="Delete breakdown"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{getMachineName(log.machineId)}</td>
                  <td>{formatDateTime(log.breakdownStartTime)}</td>
                  <td>{formatDateTime(log.breakdownEndTime)}</td>
                  <td>{formatDuration(log.durationMinutes)}</td>
                  <td>{log.failureCategory || "-"}</td>
                  <td>
                    {log.failureDescription
                      ? log.failureDescription.length > 50
                        ? `${log.failureDescription.substring(0, 50)}...`
                        : log.failureDescription
                      : "-"}
                  </td>
                  <td>
                    <span className="status-pill">
                      {getStatusName(log.statusId)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <button
                        className="btn-create-small"
                        onClick={() => handleFiveWhy(log)}
                        style={{
                          padding: "4px 8px",
                          fontSize: "0.8rem",
                          backgroundColor: "#ecfdf5",
                          color: "var(--accent-primary)",
                          border: "1px solid var(--accent-primary)",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Why
                      </button>
                      <button
                        className="btn-create-small"
                        onClick={() =>
                          handleCreateWorkOrder(log.breakdownLogId)
                        }
                        style={{
                          padding: "4px 8px",
                          fontSize: "0.8rem",
                          backgroundColor: "#eff6ff",
                          color: "#2563eb",
                          border: "1px solid #2563eb",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Work
                      </button>
                    </div>
                  </td>
                  <td>{getUserName(log.reportedBy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Filter Drawer */}
      {showFilterDrawer && (
        <div
          className="filter-drawer-overlay"
          onClick={() => setShowFilterDrawer(false)}
        >
          <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="filter-drawer-header">
              <h3>Filters</h3>
              <button
                className="btn-icon"
                onClick={() => setShowFilterDrawer(false)}
              >
                <HiOutlineXMark />
              </button>
            </div>
            <div className="filter-drawer-content">
              <div className="form-group">
                <label>Plant</label>
                <select
                  name="plantId"
                  value={filters.plantId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Plants</option>
                  {plants.map((p) => (
                    <option key={p.plantId} value={p.plantId}>
                      {p.plantName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>LINE</label>
                    <select
                      name="lineId"
                      value={filters.lineId}
                      onChange={handleFilterChange}
                      disabled={!filters.plantId}
                    >
                      <option value="">All Lines</option>
                      {lines
                        .filter(
                          (line) =>
                            !filters.plantId ||
                            line.plantId?.toString() === filters.plantId
                        )
                        .map((l) => (
                          <option key={l.lineId} value={l.lineId}>
                            {l.lineName}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Machine</label>
                    <select
                      name="machineId"
                      value={filters.machineId}
                      onChange={handleFilterChange}
                      disabled={!filters.lineId}
                    >
                      <option value="">All Machines</option>
                      {machines
                        .filter(
                          (m) =>
                            !filters.lineId ||
                            m.lineId?.toString() === filters.lineId
                        )
                        .map((m) => (
                          <option key={m.machineId} value={m.machineId}>
                            {m.machineName} ({m.machineCode})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>
              </div>


            </div>
            <div className="filter-drawer-footer">
              <button className="btn-secondary btn" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn-primary btn" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingLog ? "Edit Breakdown Log" : "New Breakdown Log"}
        customWidth="800px"
      >
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plant</label>
                <select
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Plant</option>
                  {plants.map((p) => (
                    <option key={p.plantId} value={p.plantId}>
                      {p.plantName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">LINE</label>
                <select
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleInputChange}
                  disabled={!formData.plantId}
                >
                  <option value="">All Lines</option>
                  {lines
                    .filter(
                      (l) =>
                        !formData.plantId ||
                        l.plantId?.toString() === formData.plantId
                    )
                    .map((l) => (
                      <option key={l.lineId} value={l.lineId}>
                        {l.lineName}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">
                  Machine
                </label>
                <select
                  name="machineId"
                  value={formData.machineId}
                  onChange={handleInputChange}
                  className={formErrors.machineId ? "error" : ""}
                  disabled={!formData.lineId}
                >
                  <option value="">Select Machine</option>
                  {machines
                    .filter(
                      (m) =>
                        !formData.lineId ||
                        m.lineId?.toString() === formData.lineId
                    )
                    .map((m) => (
                      <option key={m.machineId} value={m.machineId}>
                        {m.machineName} ({m.machineCode})
                      </option>
                    ))}
                </select>
                {formErrors.machineId && (
                  <span className="field-error">{formErrors.machineId}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label required">
                  Breakdown Start Time
                </label>
                <input
                  type="datetime-local"
                  name="breakdownStartTime"
                  value={formData.breakdownStartTime}
                  onChange={handleInputChange}
                  className={formErrors.breakdownStartTime ? "error" : ""}
                />
                {formErrors.breakdownStartTime && (
                  <span className="field-error">
                    {formErrors.breakdownStartTime}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">
                  Breakdown End Time
                </label>
                <input
                  type="datetime-local"
                  name="breakdownEndTime"
                  value={formData.breakdownEndTime}
                  onChange={handleInputChange}
                  className={formErrors.breakdownEndTime ? "error" : ""}
                />
                {formErrors.breakdownEndTime && (
                  <span className="field-error">
                    {formErrors.breakdownEndTime}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Failure Category</label>
                <select
                  name="failureCategory"
                  value={formData.failureCategory}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  <option value="Mechanical">Mechanical</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Hydraulic">Hydraulic</option>
                  <option value="Pneumatic">Pneumatic</option>
                  <option value="Software">Software</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  {statuses.map((s) => (
                    <option key={s.statusId} value={s.statusId}>
                      {s.statusName}
                    </option>
                  ))}
                </select>
                {statuses.length === 0 && (
                  <span className="field-error">
                    No statuses available. Please add statuses in Settings.
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Failure Description</label>
              <textarea
                name="failureDescription"
                value={formData.failureDescription}
                onChange={handleInputChange}
                placeholder="Describe the failure details..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? "Saving..." : editingLog ? "Update Breakdown" : "Create Breakdown"}
              </button>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Breakdown Log"
      >
        <div className="modal-body">
          <p>
            Are you sure you want to delete this breakdown log? This action
            cannot be undone.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => handleDelete(deleteConfirm?.breakdownLogId)}
          >
            Delete
          </button>
        </div>
      </SharedModal>
    </div >
  );
};

export default BreakdownLog;
